<template>
  <HeaderFullpage :title="t('calendarView.title')" />
  <SpinerBase
    v-if="isLoading"
    class="spiner"
  />
  <LayoutBase v-else>
    <div class="calendar">
      <div class="calendar__header">
        <div
          class="calendar__arrow"
          @click="prev"
        >
          ‹
        </div>
        <div class="calendar__title">
          {{ headerTitle }}
        </div>
        <div
          class="calendar__arrow"
          @click="next"
        >
          ›
        </div>
      </div>

      <div class="calendar__weekdays">
        <div
          v-for="item in weekDayLabels"
          :key="item"
          class="calendar__weekday"
        >
          {{ item }}
        </div>
      </div>

      <div class="calendar__days">
        <div
          v-for="day in visibleDays"
          :key="day.dayId"
          class="calendar__day"
          :class="{
            hidden: day.otherMonth,
            active: isSameDay(day.date, eventsStore.selectedDate),
            weekend: day.isWeekend,
          }"
          @click="eventsStore.selectedDate = day.date"
        >
          {{ day.date.getDate() }}
          <div
            v-if="dayHasEvents(day.date)"
            class="calendar__dot"
          />
        </div>
      </div>
    </div>

    <TabsBase
      v-model="viewMode"
      :options="viewOptions"
    />

    <div class="agenda">
      <template
        v-for="group in agenda"
        :key="group.key"
      >
        <div class="agenda__label">
          {{ group.label }}
        </div>
        <div
          v-for="item in group.items"
          :key="item.id"
          class="agenda__item"
          @click="openDetails(item)"
        >
          <AvatarBase
            :path="animalsById[item.pet_id]?.payload?.photos?.[0]?.path"
            size="46px"
          />
          <div class="agenda__info">
            <div class="agenda__name">
              {{ animalsById[item.pet_id]?.payload?.name }}
            </div>
            <div class="agenda__title">
              {{ item.title }}
            </div>
          </div>
          <div class="agenda__time">
            {{ item.all_day ? t('calendarView.allDay') : formatTime(item.date) }}
          </div>
        </div>
      </template>

      <div
        v-if="!agenda.length"
        class="agenda__empty"
      >
        {{ t('common.notResultLong') }}
      </div>
    </div>
  </LayoutBase>

  <button
    class="fab"
    @click="router.push('/events')"
  >
    +
  </button>

  <EventDetailsPane ref="detailsRef" />
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useCalendar } from 'vue-use-calendar'
import { HeaderFullpage, TabsBase, SpinerBase } from '@/UI'
import { LayoutBase } from '@/components/Layouts'
import { AvatarBase } from '@/components/AvatarBase'
import { getEvents, getAnimals } from '@/screens/EventsScreen/api'
import { useEventsStore } from '@/states/eventsStore'
import EventDetailsPane from './components/EventDetailsPane.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const eventsStore = useEventsStore()

const isLoading = ref(true)
const animalsById = ref({})
const detailsRef = ref()

const petFilter = computed(() => route.query.petId ? Number(route.query.petId) : null)

const viewMode = ref('month')
const viewOptions = computed(() => [
  { value: 'week', label: t('calendarView.week') },
  { value: 'month', label: t('calendarView.month') },
])

const weekDayLabels = computed(() => [
  t('days.mon'), t('days.tu'), t('days.wed'), t('days.th'), t('days.fri'), t('days.sat'), t('days.sun'),
])

const { useMonthlyCalendar, useWeeklyCalendar } = useCalendar({ firstDayOfWeek: 1, startOn: new Date() })
const monthly = useMonthlyCalendar({ fullWeeks: true, infinite: true })
const weekly = useWeeklyCalendar({ infinite: true })

const visibleDays = computed(() => viewMode.value === 'month' ? monthly.currentMonth.value.days : weekly.currentWeek.value.days)

const headerTitle = computed(() => {
  if (viewMode.value === 'month') {
    const { month, year } = monthly.currentMonth.value
    return new Date(year, month, 1).toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })
  }
  const { days } = weekly.currentWeek.value
  const first = days[0]?.date
  const last = days[days.length - 1]?.date
  if (!first || !last) {return ''}
  return `${first.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })} — ${last.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}`
})

const prev = () => (viewMode.value === 'month' ? monthly.prevMonth() : weekly.prevWeek())
const next = () => (viewMode.value === 'month' ? monthly.nextMonth() : weekly.nextWeek())

const isSameDay = (a, b) => a.toDateString() === b.toDateString()

const filteredEvents = computed(() => (
  petFilter.value
    ? eventsStore.events.filter((el) => el.pet_id === petFilter.value)
    : eventsStore.events
))

const eventsByDay = computed(() => {
  const map = {}
  filteredEvents.value.forEach((el) => {
    const key = new Date(el.date).toDateString()
    if (!map[key]) {map[key] = []}
    map[key].push(el)
  })
  return map
})

const dayHasEvents = (date) => !!eventsByDay.value[date.toDateString()]?.length

const formatTime = (date) => {
  const d = new Date(date)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const dayLabel = (date) => {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)
  const d = new Date(date)
  d.setHours(0, 0, 0, 0)

  if (d.getTime() === today.getTime()) {return t('calendarView.today')}
  if (d.getTime() === tomorrow.getTime()) {return t('calendarView.tomorrow')}
  return d.toLocaleDateString('ru-RU', { weekday: 'long', day: 'numeric', month: 'long' })
}

// agenda: upcoming 14 days starting from the selected date, only days that have events
const agenda = computed(() => {
  const groups = []
  const start = new Date(eventsStore.selectedDate)
  start.setHours(0, 0, 0, 0)

  for (let i = 0; i < 14; i++) {
    const date = new Date(start)
    date.setDate(date.getDate() + i)
    const items = (eventsByDay.value[date.toDateString()] || []).slice().sort((a, b) => new Date(a.date) - new Date(b.date))

    if (items.length) {
      groups.push({ key: date.toDateString(), label: dayLabel(date), items })
    }
  }

  return groups
})

const openDetails = (event) => {
  detailsRef.value.openModal({ event, pet: animalsById.value[event.pet_id] })
}

const loadData = async () => {
  const [eventsResponse, animalsResponse] = await Promise.all([getEvents(), getAnimals()])
  eventsStore.events = eventsResponse?.events || []
  animalsById.value = Object.fromEntries((animalsResponse?.animals || []).map((el) => [el.id, el]))
  isLoading.value = false
}

onMounted(() => {
  loadData()
})
</script>

<style scoped lang="scss">
.spiner {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
}

.calendar {
  padding: 10px 0 20px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__title {
    font-weight: 700;
    font-size: 18px;
    text-transform: capitalize;
  }

  &__arrow {
    cursor: pointer;
    font-size: 22px;
    padding: 0 15px;
    color: #6B7090;
  }

  &__weekdays {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    margin-bottom: 5px;
  }

  &__weekday {
    text-align: center;
    color: #6B7090;
    font-size: 12px;
  }

  &__days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  &__day {
    position: relative;
    aspect-ratio: 1/1;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;

    &.hidden {
      visibility: hidden;
      pointer-events: none;
    }

    &.weekend {
      color: #FF3B30;
    }

    &.active {
      background: #4655C4;
      color: #fff;
      border-radius: 50%;
    }
  }

  &__dot {
    position: absolute;
    bottom: 4px;
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #4655C4;
  }

  &__day.active &__dot {
    background: #fff;
  }
}

.agenda {
  margin-top: 20px;
  display: grid;
  gap: 10px;

  &__label {
    font-weight: 700;
    color: #6B7090;
    margin-top: 10px;
    text-transform: capitalize;

    &:first-child {
      margin-top: 0;
    }
  }

  &__item {
    display: grid;
    grid-template-columns: 46px 1fr auto;
    gap: 12px;
    align-items: center;
    background: #fff;
    border: 1px solid #E8EAFC;
    border-radius: 20px;
    padding: 10px 15px;
    cursor: pointer;
  }

  &__name {
    font-weight: 700;
    font-size: 15px;
  }

  &__title {
    color: #6B7090;
    font-size: 13px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  &__time {
    font-size: 14px;
    font-weight: 600;
    white-space: nowrap;
  }

  &__empty {
    text-align: center;
    color: #6B7090;
    padding: 20px;
  }
}

.fab {
  position: absolute;
  right: 20px;
  bottom: 30px;
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: #5B43EF;
  color: #fff;
  font-size: 28px;
  line-height: 1;
  box-shadow: 0px 5px 20px rgba(91, 67, 239, 0.5);
  z-index: 20;
}

[theme="dark"] {
  .agenda__item {
    background: #37363C;
    border-color: #37363C;
  }

  .calendar__weekday {
    color: #9a9fc0;
  }
}
</style>
